import { pinFoot } from './pin-foot'
import { api, getQueue, mutate, type Ack } from './api'
import { undoPatch } from './undo'
import { el, money, parseMoney, toast } from './ui'
import { currencySymbol } from './round-config'
import { cursorOnOpen, keepAboveKeyboard } from './sheet-keyboard'
import type { AnalysisPayload, DayLine } from './analysis-view'

// A day's total opened from Day totals on the Money page (design B, Liam, 24 Oct 2026: "this should also be editable
// on mac and phone at least the worker paid"; https://claude.ai/artifact/74rMZJhQMjncqL4HMiF7SN). The whole day in one
// panel, named as the day ("the add button only looks like it adds to worker paid amount but then opens and you can
// edit everything"): what the worker was paid, with one-tap amounts, Kept worked out as it is typed, and the note.
// Work done stays the visits marked done (or the total as entered), changed in the Diary, so the two never disagree.
// It saves the same day total the Diary's day does: a new one is the insert-only POST /api/day-ledger, one already there
// the version-guarded PATCH, Undo putting the old figures back. A day whose work is not all named is filled in on the
// Diary's day (diaryOnly, below).

export interface DayTotalContext {
  data: AnalysisPayload
  noPay: boolean // no worker pay in view (a one-person round): nobody is paid, the whole day is kept
  alone: boolean // a one-person round: a new total pays nobody, even with older pay in view
  label: string // the day as the table names it, "Fri 16 Oct"
  reload: () => void
  // A total just saved (not queued, not closed): Money shows a Saved tick on that day once it is drawn again.
  saved?: () => void
}

export function lineWorker(line: DayLine): string {
  return (line.kind === 'total' ? line.row.worker ?? '' : line.row.worker).trim()
}

export function lineWorkDone(line: DayLine): number {
  return line.kind === 'total' ? line.row.gross_pence : line.row.pence
}

// What the same person was paid on their last day with pay before this one, for the one-tap amount. Pay only: a day
// with nothing paid says nothing about a day rate. None in view: no amount offered, never a made-up one.
export function lastPaid(data: AnalysisPayload, worker: string, date: string): number | null {
  const name = worker.trim().toLowerCase()
  if (!name) return null
  const prior = data.days
    .filter((d) => (d.worker ?? '').trim().toLowerCase() === name && d.date < date && d.worker_pence > 0)
    .sort((a, b) => (a.date < b.date ? 1 : -1))
  return prior[0]?.worker_pence ?? null
}

// A new total from Money only where it counts exactly the work it says (Astra's reviews, 24 Oct 2026). Work nobody was
// put on goes with the day's first total, and on a one-person round the owner's total takes in every name without its
// own (functions/lib/analysis.ts), so a total saved here beside such work would leave money out, or two phones could
// each count it once. Those days are filled in on the Diary's day, which gives the visits a name first ("Give them to")
// and counts a one-person day together; Money says so and opens it.
export function diaryOnly(data: AnalysisPayload, line: DayLine, alone: boolean): string | null {
  if (line.kind === 'total') return null
  const others = [...(data.missing_days ?? []), ...(data.finished_days ?? [])].filter((m) => m.date === line.date && m !== line.row)
  if (!line.row.worker.trim()) return 'Nobody was put on this work. Give it to someone on the Diary’s day first, then fill their day in here.'
  const nobody = others.find((m) => !m.worker.trim())
  if (nobody) return `This day also has ${money(nobody.pence)} of work nobody was put on. Give it to someone on the Diary’s day first, so none of it is left out.`
  if (alone && others.length) return 'Other work that day has no total yet. Fill the day in on the Diary’s day, which counts it all together.'
  return null
}

// Already saved on this phone and waiting for signal: not a second time (Astra's review: reopened, it made two).
function waitingOnPhone(date: string, worker: string): boolean {
  return getQueue().some((q) => {
    const b = q.body as { date?: unknown; worker?: unknown } | null
    return q.method === 'POST' && q.path === '/api/day-ledger' && b?.date === date && String(b?.worker ?? '').trim().toLowerCase() === worker.toLowerCase()
  })
}

export function openDayTotal(line: DayLine, ctx: DayTotalContext, opener: HTMLElement): void {
  const total = line.kind === 'total' ? line.row : null
  const worker = lineWorker(line)
  const workDone = lineWorkDone(line)
  const cleans = line.row.cleans
  // A one-person round pays nobody on a new total; a total from before keeps its pay to change (Astra's review).
  const payShown = !ctx.noPay && (!ctx.alone || (total !== null && total.worker_pence > 0))
  // Unknown pay stays unknown (not £0) until something is typed, and Undo puts the unknown back.
  const paidBefore = total ? (total.paid_unknown ? null : total.worker_pence) : null
  const noteBefore = total?.notes ?? ''
  const waiting = !total && waitingOnPhone(line.date, worker)
  const elsewhere = waiting ? `${ctx.label} is saved on this phone and goes in when there’s signal.` : diaryOnly(ctx.data, line, ctx.alone)

  const dialog = el('dialog', { class: 'booking-dialog day-total-dialog', 'aria-labelledby': 'day-total-title' }) as HTMLDialogElement
  const close = el('button', { type: 'button', 'aria-label': 'Close' }, 'Close')
  const title = el('h2', { id: 'day-total-title' }, `${ctx.label} · the day`)
  const body = el('div', { class: 'day-total-body' })
  body.append(el('p', { class: 'day-total-facts' }, `${worker || 'Nobody put on'} · ${cleans} ${cleans === 1 ? 'clean' : 'cleans'} · work done ${money(workDone)}`))
  const free = keepAboveKeyboard(dialog)
  const leave = (): void => { if (dialog.open) dialog.close() }
  const show = (): void => {
    close.addEventListener('click', () => dialog.close())
    dialog.append(el('header', { class: 'booking-head' }, title, close), body)
    // Leaving the screen (the Diary link, Back) closes it: it would otherwise stay over the next screen (Astra's review).
    window.addEventListener('hashchange', leave)
    dialog.addEventListener('close', () => {
      free(); window.removeEventListener('hashchange', leave); dialog.remove()
      if (opener.isConnected) opener.focus({ preventScroll: true })
    })
    document.body.append(dialog)
    dialog.showModal()
  }
  if (elsewhere) {
    const go = waiting
      ? el('button', { type: 'button', class: 'btn-primary day-total-go' }, 'Close')
      : el('a', { class: 'btn-primary day-total-go', href: `#/schedule?from=${line.date}` }, 'Open the day in the Diary')
    go.addEventListener('click', leave)
    body.append(el('p', { class: 'day-total-why' }, elsewhere), go)
    show()
    return
  }

  // What they were paid, with the one-tap amounts.
  const paid = el('input', { type: 'text', inputmode: 'decimal', autocomplete: 'off', class: 'day-total-amount', 'aria-describedby': 'day-total-kept' }) as HTMLInputElement
  if (paidBefore !== null) paid.value = (paidBefore / 100).toFixed(2)
  const amounts = el('div', { class: 'day-total-amounts', role: 'group', 'aria-label': 'Amounts' })
  const kept = el('p', { class: 'day-total-kept', id: 'day-total-kept', 'aria-live': 'polite' })
  const error = el('p', { class: 'form-error', role: 'alert' })
  const cutTyped = (): number | null => {
    if (!payShown) return 0
    try { return parseMoney(paid.value) } catch { return null }
  }
  const sayKept = (): void => {
    const cut = cutTyped()
    kept.textContent = ''
    if (!payShown) { kept.append(el('span', {}, 'Nobody paid: the whole day is kept'), el('strong', {}, `Kept ${money(workDone)}`)); return }
    if (cut === null) { kept.append(el('span', {}, `${money(workDone)} work done`), el('strong', {}, 'Kept —')); return }
    kept.append(el('span', {}, `${money(workDone)} work done − ${money(cut)}`), el('strong', {}, `Kept ${money(workDone - cut)}`))
  }
  const sayChips = (): void => {
    const cut = cutTyped()
    for (const b of amounts.children) b.setAttribute('aria-pressed', String(cut !== null && String(cut) === (b as HTMLElement).dataset.pence))
  }
  const chip = (text: string, pence: number): HTMLButtonElement => {
    const b = el('button', { type: 'button', class: 'day-total-chip', 'aria-pressed': 'false' }, text) as HTMLButtonElement
    b.addEventListener('click', () => { paid.value = (pence / 100).toFixed(2); sayKept(); sayChips(); error.textContent = '' })
    b.dataset.pence = String(pence)
    return b
  }
  paid.addEventListener('input', () => { sayKept(); sayChips(); error.textContent = '' })
  if (payShown) {
    const last = lastPaid(ctx.data, worker, line.date)
    if (last !== null) amounts.append(chip(`${money(last)} · last time`, last))
    amounts.append(chip('Nothing paid', 0))
    sayChips()
    body.append(el('label', { class: 'day-total-field' }, el('span', { class: 'day-total-label' }, worker ? `What ${worker} was paid` : 'What was paid'),
      el('span', { class: 'day-total-money' }, el('span', { 'aria-hidden': 'true' }, currencySymbol()), paid)), amounts)
  }
  sayKept()
  body.append(kept)

  const note = el('input', { type: 'text', class: 'day-total-note', maxlength: '2000', placeholder: 'Left early, rain' }) as HTMLInputElement
  note.value = noteBefore
  body.append(el('label', { class: 'day-total-field' }, el('span', { class: 'day-total-label' }, 'Note (optional)'), note))
  const save = el('button', { type: 'button', class: 'btn-primary day-total-save' }, `Save ${ctx.label}`) as HTMLButtonElement
  const diary = el('a', { class: 'day-total-diary', href: `#/schedule?from=${line.date}` }, total ? 'Work done wrong? Change it on the day in the Diary' : 'Work done wrong? Change a visit on the day in the Diary')
  diary.addEventListener('click', leave)
  body.append(error, pinFoot(save, diary))

  // Saved with no signal (or the server busy): it waits on this phone and goes in by itself, so the panel closes and
  // cannot be sent twice (Astra's review: a retry under another name made two totals).
  let queued = false
  const onQueued = (): void => { queued = true; leave(); toast(`${ctx.label} is saved on this phone. It goes in when there’s signal.`) }
  const finish = (): void => { leave(); ctx.saved?.(); ctx.reload() }
  save.addEventListener('click', () => {
    error.textContent = ''
    let cut: number | null
    try { cut = payShown ? parseMoney(paid.value) : (total ? paidBefore : 0) } catch (e) { error.textContent = `Type ${(e as Error).message}.`; return }
    const noteNow = note.value.trim()
    if (cut === null && !total) { error.textContent = 'Type what was paid, or tap Nothing paid.'; paid.focus(); return }
    // A typo that makes a loss is checked; correcting a day already at a loss (a day rate over a short day, kept since
    // 29 Oct 2026) is not refused (Astra's review).
    if (payShown && cut !== null && cut > workDone && cut !== paidBefore && (paidBefore ?? 0) <= workDone) {
      error.textContent = `That is more than the day’s work done (${money(workDone)}). Check the amount, or change the work done in the Diary.`
      return
    }
    if (total) {
      if (total.id == null || total.version == null) { error.textContent = 'This day can only be changed in the Diary for now.'; return }
      const change: Record<string, unknown> = {}
      if (payShown && cut !== paidBefore) change.worker_cut_pence = cut
      // A different amount on a day already ticked paid is paid again on the close-out, never left looking settled
      // (Astra's build review, 0097).
      if ('worker_cut_pence' in change && total.paid_at) { change.paid_at = false; change.paid_how = null }
      if (noteNow !== noteBefore.trim()) change.notes = noteNow || null
      if (!Object.keys(change).length) { leave(); return }
      // Undo puts back what was there: an unknown pay as unknown, with the kept it had beside it.
      const back: Record<string, unknown> = {}
      if ('worker_cut_pence' in change) {
        back.worker_cut_pence = paidBefore
        if (paidBefore === null) back.our_cut_pence = total.kept_unknown ? null : total.kept_pence
      }
      if ('notes' in change) back.notes = total.notes ?? null
      if ('paid_at' in change) { back.paid_at = true; back.paid_how = total.paid_how ?? null }
      // An old pay above the work done is refused by the server, so Undo could not put it back: none is offered.
      const undoable = !('worker_cut_pence' in change) || paidBefore === null || paidBefore <= total.gross_pence
      save.disabled = true
      const path = `/api/day-ledger/${total.id}`
      const version = total.version
      void mutate({
        label: `Change the day total ${line.date} (${worker || 'unassigned work'})`, method: 'PATCH', path,
        body: { version, ...change },
        onConflict: () => { error.textContent = 'This day was changed somewhere else meanwhile. Close this to see it as it is now.'; ctx.reload() },
        onQueued,
      }).then((ack: Ack) => {
        if (queued) { ctx.reload(); return }
        if (!ack.ok && ack.status >= 400) { if (ack.status !== 409) { error.textContent = ack.error ?? 'That didn’t save. Try again.'; save.disabled = false } return }
        finish()
        if (undoable) undoPatch({ text: `${ctx.label} saved`, label: `Undo the day total ${line.date}`, path, ack, version, body: back, after: ctx.reload })
        else toast(`${ctx.label} saved. The old pay was more than the work done, so it can’t be put back here.`)
      })
      return
    }
    save.disabled = true
    void (async () => {
      // Read the day again just before saving: a visit given to someone else, more work done, a total put in or the
      // round switched to one person on another phone while this was open means the figures here are not the day's
      // any more (Astra's review). With no signal it cannot be read, and the save waits on the phone as it is.
      const now = await api<AnalysisPayload>('/api/analysis', 'analysis').catch(() => null)
      if (now && stale(now)) {
        error.textContent = 'This day changed on another phone while this was open. Close this to see it as it is now.'
        ctx.reload()
        return
      }
      send()
    })()
  })
  // The day as the panel opened it, against the day as it is now.
  const stale = (now: AnalysisPayload): boolean => {
    const name = worker.toLowerCase()
    const same = [...(now.missing_days ?? []), ...(now.finished_days ?? [])].find((m) => m.date === line.date && m.worker.trim().toLowerCase() === name)
    const aloneNow = 'finished_days' in now // the payload carries finished days for a one-person round only
    if (!same || same.pence !== workDone || same.cleans !== cleans || aloneNow !== ctx.alone) return true
    if (now.days.some((d) => d.date === line.date && (d.worker ?? '').trim().toLowerCase() === name)) return true
    return diaryOnly(now, { ...line, row: same } as DayLine, aloneNow) !== null
  }
  const send = (): void => {
    const cut = payShown ? parseMoney(paid.value) : 0
    const noteNow = note.value.trim()
    void mutate({
      label: `Day total ${line.date} (${worker})`, method: 'POST', path: '/api/day-ledger',
      body: { date: line.date, worker, gross_pence: workDone, worker_cut_pence: cut, ...(noteNow ? { notes: noteNow } : {}) },
      onQueued,
    }).then((ack: Ack) => {
      if (queued) { ctx.reload(); return }
      // Put in on the Diary or another phone meanwhile: never written over. Save stays off; the page behind shows it, to
      // change with Change this day.
      if (!ack.ok && ack.status === 409) { error.textContent = `${ctx.label} already has a total for ${worker}, put in on the Diary or another phone. Close this to see it, then Change this day.`; ctx.reload(); return }
      if (!ack.ok && ack.status >= 400) { error.textContent = ack.error ?? 'That didn’t save. Try again.'; save.disabled = false; return }
      finish()
    })
  }

  show()
  if (payShown) cursorOnOpen(paid)
}
